
import React, { createContext, ReactNode, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Notification } from '@/types/notifications';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  isUpdating: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const refreshNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      const typedNotifications = data.map(notification => ({
        id: notification.id,
        user_id: notification.user_id,
        title: notification.title,
        body: notification.body,
        type: notification.type as 'info' | 'success' | 'warning' | 'error',
        seen: Boolean(notification.seen),
        created_at: notification.created_at,
        link: notification.link
      }));
      
      setNotifications(typedNotifications);
      const unseenCount = typedNotifications.filter(n => !n.seen).length;
      setUnreadCount(unseenCount);
      
      // Save the count to session storage for persistence across refreshes
      sessionStorage.setItem('notification_unread_count', unseenCount.toString());
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch of notifications
    refreshNotifications();
    
    // Set up realtime subscription
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        refreshNotifications();
      } else if (event === 'SIGNED_OUT') {
        setNotifications([]);
        setUnreadCount(0);
        sessionStorage.removeItem('notification_unread_count');
      }
    });
    
    // Listen for new notifications
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        async (payload) => {
          // Get current user to check if notification is for them
          const { data: userData } = await supabase.auth.getUser();
          if (userData?.user?.id === payload.new.user_id) {
            const newNotification = {
              id: payload.new.id,
              user_id: payload.new.user_id,
              title: payload.new.title,
              body: payload.new.body,
              type: payload.new.type as 'info' | 'success' | 'warning' | 'error',
              seen: Boolean(payload.new.seen),
              created_at: payload.new.created_at,
              link: payload.new.link
            };
            
            // Update state with new notification
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => {
              const newCount = prev + 1;
              sessionStorage.setItem('notification_unread_count', newCount.toString());
              return newCount;
            });
          }
        }
      )
      .subscribe();

    return () => {
      // Clean up subscriptions
      authListener.subscription.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [refreshNotifications]);

  const markAsRead = async (notificationId: string) => {
    try {
      setIsUpdating(true);
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData?.user?.id) return;
      
      const { error } = await supabase
        .from('notifications')
        .update({ seen: true })
        .eq('id', notificationId)
        .eq('user_id', userData.user.id);

      if (error) throw error;
      
      // Update local state
      setNotifications(prev => prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, seen: true } 
          : notification
      ));
      
      // Update unread count in state and session storage
      const newUnreadCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newUnreadCount);
      sessionStorage.setItem('notification_unread_count', newUnreadCount.toString());
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      setIsUpdating(true);
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData?.user?.id) return;
      
      const { error } = await supabase
        .from('notifications')
        .update({ seen: true })
        .eq('user_id', userData.user.id)
        .eq('seen', false);

      if (error) throw error;
      
      // Update local state
      setNotifications(prev => prev.map(notification => ({ ...notification, seen: true })));
      
      // Update unread count in state and session storage
      setUnreadCount(0);
      sessionStorage.setItem('notification_unread_count', '0');
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        isUpdating,
        markAsRead,
        markAllAsRead,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  
  return context;
}
